/**
 * Product gallery
 *
 * Features:
 *  - Main carousel with thumbnails (vertical on desktop, horizontal on mobile)
 *  - Inline hover-zoom on desktop (cursor-follow magnifier)
 *  - Touch swipe between slides on mobile
 *  - Keyboard navigation (arrows, Home/End, Esc)
 *  - Fullscreen lightbox with pinch-zoom, drag-to-pan, double-tap, mouse wheel zoom
 *  - Focus trap inside lightbox
 *  - No external dependencies
 */
(function () {
  'use strict';

  if (window.__productGalleryLoaded) return;
  window.__productGalleryLoaded = true;

  var SWIPE_THRESHOLD = 40;
  var DESKTOP_BREAKPOINT = 1025;
  var ZOOM_LEVEL = 2.2;
  var LIGHTBOX_ZOOM_IN = 2.5;
  var LIGHTBOX_MAX_SCALE = 4; // hard cap on how far the lightbox can zoom in
  var DRAG_THRESHOLD = 4; // pixels of movement before a click is treated as a drag

  function isDesktop() {
    return window.matchMedia('(min-width: ' + DESKTOP_BREAKPOINT + 'px)').matches;
  }

  function isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  /* ------------------------------------------------------------------ *
   * Main gallery
   * ------------------------------------------------------------------ */
  function ProductGallery(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-gallery-viewport]');
    this.track = root.querySelector('[data-gallery-track]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('.product-gallery__slide'));
    this.thumbsTrack = root.querySelector('[data-thumbs-track]');
    this.thumbs = this.thumbsTrack
      ? Array.prototype.slice.call(this.thumbsTrack.querySelectorAll('.product-gallery__thumb'))
      : [];
    this.prevBtn = root.querySelector('[data-gallery-prev]');
    this.nextBtn = root.querySelector('[data-gallery-next]');
    this.thumbsPrev = root.querySelector('[data-thumbs-prev]');
    this.thumbsNext = root.querySelector('[data-thumbs-next]');
    this.counterCurrent = root.querySelector('[data-counter-current]');
    this.zoomTriggers = Array.prototype.slice.call(root.querySelectorAll('[data-zoom-trigger]'));
    this.loader = root.querySelector('[data-gallery-loader]');

    this.index = 0;
    this.count = this.slides.length;
    this.dragging = false;
    this.startX = 0;
    this.startY = 0;
    this.deltaX = 0;
    this.lockedAxis = null;

    this._zoomLayer = null;
    this._zoomImage = null;

    this.bind();
    this.update(0, false);
    this.updateThumbOverflow();
  }

  ProductGallery.prototype.bind = function () {
    var self = this;

    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.go(self.index - 1); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.go(self.index + 1); });

    // Event delegation so thumbs/zoom triggers keep working after setImages rebuilds the DOM
    if (this.thumbsTrack) {
      this.thumbsTrack.addEventListener('click', function (e) {
        var thumb = e.target.closest('.product-gallery__thumb');
        if (!thumb) return;
        var idx = parseInt(thumb.getAttribute('data-thumb-index'), 10);
        self.go(idx);
      });
    }

    if (this.track) {
      this.track.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-zoom-trigger]');
        if (!trigger) return;
        e.preventDefault();
        var idx = self.zoomTriggers.indexOf(trigger);
        if (idx < 0) return;
        if (window.__klassikLightbox) {
          window.__klassikLightbox.open(self.collectImages(), idx);
        }
      });
    }

    if (this.thumbsPrev) {
      this.thumbsPrev.addEventListener('click', function () { self.scrollThumbs(-1); });
    }
    if (this.thumbsNext) {
      this.thumbsNext.addEventListener('click', function () { self.scrollThumbs(1); });
    }

    // Touch swipe on main viewport
    if (this.viewport) {
      this.viewport.addEventListener('touchstart', function (e) { self.onTouchStart(e); }, { passive: true });
      this.viewport.addEventListener('touchmove', function (e) { self.onTouchMove(e); }, { passive: false });
      this.viewport.addEventListener('touchend', function (e) { self.onTouchEnd(e); });
      this.viewport.addEventListener('touchcancel', function () { self.cancelDrag(); });
    }

    // Keyboard nav when gallery is focused
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.go(self.index - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); self.go(self.index + 1); }
      else if (e.key === 'Home') { e.preventDefault(); self.go(0); }
      else if (e.key === 'End') { e.preventDefault(); self.go(self.count - 1); }
    });

    // Bind hover-zoom on current zoom triggers (mouseenter/leave don't bubble, can't delegate)
    this.bindHoverZoom();

    // Resize handler — disable hover-zoom and refresh overflow state
    window.addEventListener('resize', function () {
      self.disableHoverZoom();
      self.updateThumbOverflow();
    });
  };

  ProductGallery.prototype.bindHoverZoom = function () {
    var self = this;
    this.zoomTriggers.forEach(function (trigger) {
      if (trigger.__hoverBound) return;
      trigger.__hoverBound = true;
      var img = trigger.querySelector('.product-gallery__image');
      if (!img) return;
      trigger.addEventListener('mouseenter', function () { self.enableHoverZoom(trigger, img); });
      trigger.addEventListener('mouseleave', function () { self.disableHoverZoom(); });
      trigger.addEventListener('mousemove',  function (e) { self.moveHoverZoom(e, trigger); });
    });
  };

  // Replace the gallery's images dynamically (used when variant changes).
  // `templates` is an array of URL strings, each containing a "{width}" placeholder
  // (matching the format Klassik already uses in data-images on variant options).
  ProductGallery.prototype.setImages = function (templates) {
    if (!templates || !templates.length) return;
    var self = this;

    var clean = templates.map(function (t) { return (t || '').trim(); }).filter(Boolean);
    if (!clean.length) return;

    var total = clean.length;

    function buildSlide(template, index) {
      var src      = template.replace('{width}', '1200');
      var s600     = template.replace('{width}', '600');
      var s900     = template.replace('{width}', '900');
      var s1200    = template.replace('{width}', '1200');
      var s1600    = template.replace('{width}', '1600');
      var s2000    = template.replace('{width}', '2000');
      var s2400    = template.replace('{width}', '2400');
      var srcset = s600 + ' 600w, ' + s900 + ' 900w, ' + s1200 + ' 1200w, ' + s1600 + ' 1600w, ' + s2000 + ' 2000w';
      var hidden = index === 0 ? '' : ' aria-hidden="true"';
      var firstAttrs = index === 0 ? ' fetchpriority="high"' : '';
      var loading = index === 0 ? 'eager' : 'lazy';
      return '<figure class="product-gallery__slide' + (index === 0 ? ' is-active' : '') +
             '" data-slide-index="' + index + '"' + hidden + '>' +
               '<button type="button" class="product-gallery__zoom-trigger" data-zoom-trigger ' +
                 'data-full-src="' + s2400 + '" data-full-width="2400" data-full-height="2400" data-alt="" ' +
                 'aria-label="Åbn billede ' + (index + 1) + ' i fuld skærm">' +
                 '<img class="product-gallery__image" src="' + src + '" srcset="' + srcset + '" ' +
                   'sizes="(max-width: 1024px) 100vw, 60vw" data-zoom-src="' + s2400 + '" ' +
                   'alt="" loading="' + loading + '" decoding="async"' + firstAttrs + '>' +
               '</button>' +
               '<span class="product-gallery__zoom-hint" aria-hidden="true">' +
                 '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>' +
               '</span>' +
             '</figure>';
    }

    function buildThumb(template, index) {
      var t120 = template.replace('{width}', '120');
      var t240 = template.replace('{width}', '240');
      var t160 = template.replace('{width}', '160');
      return '<button type="button" class="product-gallery__thumb' + (index === 0 ? ' is-active' : '') +
             '" data-thumb-index="' + index + '" aria-label="Vis billede ' + (index + 1) + ' af ' + total + '"' +
             (index === 0 ? ' aria-current="true"' : '') + '>' +
               '<img src="' + t160 + '" srcset="' + t120 + ' 1x, ' + t240 + ' 2x" width="80" height="80" alt="" loading="lazy" decoding="async">' +
             '</button>';
    }

    var trackHtml = clean.map(buildSlide).join('');
    var thumbsHtml = clean.map(buildThumb).join('');

    if (this.track) this.track.innerHTML = trackHtml;
    if (this.thumbsTrack) this.thumbsTrack.innerHTML = thumbsHtml;

    // Refresh element references
    this.slides = Array.prototype.slice.call(this.root.querySelectorAll('.product-gallery__slide'));
    this.thumbs = this.thumbsTrack
      ? Array.prototype.slice.call(this.thumbsTrack.querySelectorAll('.product-gallery__thumb'))
      : [];
    this.zoomTriggers = Array.prototype.slice.call(this.root.querySelectorAll('[data-zoom-trigger]'));
    this.count = this.slides.length;
    this.index = 0;

    // Update UI state
    var totalEl = this.root.querySelector('[data-counter-total]');
    if (totalEl) totalEl.textContent = String(this.count);
    var currentEl = this.root.querySelector('[data-counter-current]');
    if (currentEl) currentEl.textContent = '1';

    this.update(0, false);
    this.disableHoverZoom();
    this.bindHoverZoom();
    requestAnimationFrame(function () { self.updateThumbOverflow(); });
  };

  ProductGallery.prototype.updateThumbOverflow = function () {
    if (!this.thumbsTrack) return;
    var thumbsContainer = this.thumbsTrack.parentNode;
    var vertical = isDesktop();
    var hasOverflow = vertical
      ? this.thumbsTrack.scrollHeight > this.thumbsTrack.clientHeight + 1
      : this.thumbsTrack.scrollWidth > this.thumbsTrack.clientWidth + 1;
    if (hasOverflow) {
      if (thumbsContainer) thumbsContainer.setAttribute('data-has-overflow', '');
    } else {
      if (thumbsContainer) thumbsContainer.removeAttribute('data-has-overflow');
    }
  };

  ProductGallery.prototype.collectImages = function () {
    var self = this;
    return this.zoomTriggers.map(function (trigger, i) {
      var thumbImg = self.thumbs[i] ? self.thumbs[i].querySelector('img') : null;
      return {
        src: trigger.getAttribute('data-full-src'),
        thumb: thumbImg ? (thumbImg.currentSrc || thumbImg.src) : trigger.getAttribute('data-full-src'),
        width: parseInt(trigger.getAttribute('data-full-width'), 10) || 2000,
        height: parseInt(trigger.getAttribute('data-full-height'), 10) || 2000,
        alt: trigger.getAttribute('data-alt') || ''
      };
    });
  };

  ProductGallery.prototype.go = function (target) {
    if (this.count <= 1) return;
    target = ((target % this.count) + this.count) % this.count;
    this.update(target, true);
  };

  ProductGallery.prototype.update = function (target, animate) {
    this.index = target;
    if (this.track) {
      this.track.style.transition = animate ? '' : 'none';
      this.track.style.transform = 'translate3d(' + (-target * 100) + '%, 0, 0)';
      if (!animate) {
        // Force reflow then re-enable transitions
        void this.track.offsetWidth;
        this.track.style.transition = '';
      }
    }
    this.slides.forEach(function (slide, i) {
      var active = i === target;
      slide.classList.toggle('is-active', active);
      if (active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });
    this.thumbs.forEach(function (thumb, i) {
      var active = i === target;
      thumb.classList.toggle('is-active', active);
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    if (this.counterCurrent) this.counterCurrent.textContent = String(target + 1);
    this.scrollActiveThumbIntoView();
    this.checkSlideLoading(target);
    this.preloadZoomImage(target);
  };

  // Preload the high-res (2400px) zoom image for a slide so desktop hover-zoom is instant.
  ProductGallery.prototype.preloadZoomImage = function (index) {
    if (!isDesktop() || isTouchDevice()) return;
    var trigger = this.zoomTriggers[index];
    var imgEl = trigger && trigger.querySelector('.product-gallery__image');
    var src = imgEl && imgEl.getAttribute('data-zoom-src');
    if (!src) return;
    this._zoomCache = this._zoomCache || {};
    if (this._zoomCache[src]) return;
    var pre = new Image();
    pre.src = src;
    this._zoomCache[src] = pre;
  };

  // Show a spinner over the viewport while a (lazy-loaded) slide image is still loading
  ProductGallery.prototype.checkSlideLoading = function (index) {
    if (!this.loader) return;
    var slide = this.slides[index];
    var img = slide && slide.querySelector('.product-gallery__image');
    if (!img) { this.loader.hidden = true; return; }
    if (img.complete && img.naturalWidth > 0) {
      this.loader.hidden = true;
      return;
    }
    var self = this;
    this.loader.hidden = false;
    var done = function () {
      self.loader.hidden = true;
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
    };
    img.addEventListener('load', done);
    img.addEventListener('error', done);
  };

  ProductGallery.prototype.scrollActiveThumbIntoView = function () {
    if (!this.thumbsTrack) return;
    var active = this.thumbs[this.index];
    if (!active) return;
    var vertical = isDesktop();
    if (vertical) {
      var thumbRect = active.getBoundingClientRect();
      var containerRect = this.thumbsTrack.getBoundingClientRect();
      if (thumbRect.top < containerRect.top || thumbRect.bottom > containerRect.bottom) {
        var offset = active.offsetTop - this.thumbsTrack.clientHeight / 2 + active.offsetHeight / 2;
        this.thumbsTrack.scrollTo({ top: offset, behavior: 'smooth' });
      }
    } else {
      var hRect = active.getBoundingClientRect();
      var hContainer = this.thumbsTrack.getBoundingClientRect();
      if (hRect.left < hContainer.left || hRect.right > hContainer.right) {
        var hOffset = active.offsetLeft - this.thumbsTrack.clientWidth / 2 + active.offsetWidth / 2;
        this.thumbsTrack.scrollTo({ left: hOffset, behavior: 'smooth' });
      }
    }
  };

  ProductGallery.prototype.scrollThumbs = function (direction) {
    if (!this.thumbsTrack) return;
    var vertical = isDesktop();
    var amount = vertical ? this.thumbsTrack.clientHeight * 0.7 : this.thumbsTrack.clientWidth * 0.7;
    if (vertical) {
      this.thumbsTrack.scrollBy({ top: direction * amount, behavior: 'smooth' });
    } else {
      this.thumbsTrack.scrollBy({ left: direction * amount, behavior: 'smooth' });
    }
  };

  // Touch handlers ---------------------------------------------------
  ProductGallery.prototype.onTouchStart = function (e) {
    if (this.count <= 1) return;
    if (e.touches.length !== 1) return;
    this.dragging = true;
    this.startX = e.touches[0].clientX;
    this.startY = e.touches[0].clientY;
    this.deltaX = 0;
    this.lockedAxis = null;
    if (this.track) this.track.style.transition = 'none';
  };

  ProductGallery.prototype.onTouchMove = function (e) {
    if (!this.dragging) return;
    var dx = e.touches[0].clientX - this.startX;
    var dy = e.touches[0].clientY - this.startY;
    if (!this.lockedAxis) {
      // Don't decide — or preventDefault — until the gesture has clearly
      // committed to one direction. Calling preventDefault on the first tiny
      // touchmove cancels the browser's native vertical scroll for the rest of
      // the gesture, which made downward scrolls feel like a stuck swipe.
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      // Bias toward vertical scroll: only treat it as a horizontal swipe when
      // the horizontal movement clearly dominates. A slightly skewed downward
      // drag should still scroll the page rather than slide the gallery.
      this.lockedAxis = Math.abs(dx) > Math.abs(dy) * 1.5 ? 'x' : 'y';
    }
    if (this.lockedAxis !== 'x') return; // vertical → let the page scroll
    e.preventDefault();
    this.deltaX = dx;
    if (this.track) {
      var pct = (this.deltaX / this.viewport.clientWidth) * 100;
      this.track.style.transform = 'translate3d(calc(' + (-this.index * 100) + '% + ' + pct + '%), 0, 0)';
    }
  };

  ProductGallery.prototype.onTouchEnd = function () {
    if (!this.dragging) return;
    this.dragging = false;
    if (this.track) this.track.style.transition = '';
    if (this.lockedAxis !== 'x') { this.update(this.index, true); return; }
    if (this.deltaX < -SWIPE_THRESHOLD && this.index < this.count - 1) {
      this.go(this.index + 1);
    } else if (this.deltaX > SWIPE_THRESHOLD && this.index > 0) {
      this.go(this.index - 1);
    } else {
      this.update(this.index, true);
    }
  };

  ProductGallery.prototype.cancelDrag = function () {
    this.dragging = false;
    if (this.track) this.track.style.transition = '';
    this.update(this.index, true);
  };

  // Hover zoom -------------------------------------------------------
  ProductGallery.prototype.enableHoverZoom = function (trigger, img) {
    if (!isDesktop() || isTouchDevice()) return;
    var src = img.getAttribute('data-zoom-src') || img.currentSrc || img.src;
    this.disableHoverZoom();
    var layer = document.createElement('div');
    layer.className = 'product-gallery__zoom-layer';
    layer.style.backgroundImage = 'url("' + src + '")';
    trigger.appendChild(layer);
    this._zoomLayer = layer;
    trigger.classList.add('is-zooming');

    // Show a loading state until the high-res zoom image is ready
    this._zoomCache = this._zoomCache || {};
    var probe = this._zoomCache[src];
    if (!probe) { probe = new Image(); probe.src = src; this._zoomCache[src] = probe; }
    if (probe.complete && probe.naturalWidth > 0) {
      layer.classList.add('is-ready');
    } else {
      layer.classList.add('is-loading');
      probe.addEventListener('load', function onLoad() {
        layer.classList.add('is-ready');
        layer.classList.remove('is-loading');
        probe.removeEventListener('load', onLoad);
      });
    }
  };

  ProductGallery.prototype.moveHoverZoom = function (e, trigger) {
    if (!this._zoomLayer) return;
    var rect = trigger.getBoundingClientRect();
    var x = ((e.clientX - rect.left) / rect.width) * 100;
    var y = ((e.clientY - rect.top) / rect.height) * 100;
    this._zoomLayer.style.backgroundPosition = clamp(x, 0, 100) + '% ' + clamp(y, 0, 100) + '%';
    this._zoomLayer.style.backgroundSize = (ZOOM_LEVEL * 100) + '%';
  };

  ProductGallery.prototype.disableHoverZoom = function () {
    if (this._zoomLayer && this._zoomLayer.parentNode) {
      this._zoomLayer.parentNode.classList.remove('is-zooming');
      this._zoomLayer.parentNode.removeChild(this._zoomLayer);
    }
    this._zoomLayer = null;
  };

  /* ------------------------------------------------------------------ *
   * Lightbox (singleton, shared across galleries on the page)
   * ------------------------------------------------------------------ */
  function Lightbox(root) {
    this.root = root;
    this.image = root.querySelector('[data-lightbox-image]');
    this.scroller = root.querySelector('[data-lightbox-scroller]');
    this.stage = root.querySelector('[data-lightbox-stage]');
    this.closeBtn = root.querySelector('[data-lightbox-close]');
    this.prevBtn = root.querySelector('[data-lightbox-prev]');
    this.nextBtn = root.querySelector('[data-lightbox-next]');
    this.currentEl = root.querySelector('[data-lightbox-current]');
    this.totalEl = root.querySelector('[data-lightbox-total]');
    this.thumbsEl = root.querySelector('[data-lightbox-thumbs]');
    this.loader = root.querySelector('[data-lightbox-loader]');

    this.images = [];
    this.index = 0;
    this.scale = 1;
    this.translate = { x: 0, y: 0 };
    this.lastTap = 0;
    this.pinchStartDistance = 0;
    this.pinchStartScale = 1;
    this.pinchAnchor = { x: 0, y: 0 };
    this.scrollerCenter = { x: 0, y: 0 };
    this.panStart = null;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.swiping = false;
    this.swipeLocked = null;
    this._previouslyFocused = null;

    // Desktop drag state
    this.mouseDown = false;
    this.mouseDragged = false;
    this.mouseStart = null;

    this.bind();
  }

  Lightbox.prototype.bind = function () {
    var self = this;

    // Controls — each stops propagation so the root click handler doesn't also fire
    function stop(e) { e.stopPropagation(); }
    if (this.closeBtn) this.closeBtn.addEventListener('click', function (e) { stop(e); self.close(); });
    if (this.prevBtn)  this.prevBtn.addEventListener('click',  function (e) { stop(e); self.go(self.index - 1); });
    if (this.nextBtn)  this.nextBtn.addEventListener('click',  function (e) { stop(e); self.go(self.index + 1); });

    // The image swallows clicks — mouse interactions on the image are handled by mouseup below.
    if (this.image) this.image.addEventListener('click', stop);

    // Thumbnail strip — click a thumb to jump to that image (delegated so it survives rebuilds)
    if (this.thumbsEl) {
      this.thumbsEl.addEventListener('click', function (e) {
        stop(e);
        var thumb = e.target.closest('[data-lb-thumb-index]');
        if (!thumb) return;
        var idx = parseInt(thumb.getAttribute('data-lb-thumb-index'), 10);
        if (!isNaN(idx)) self.go(idx);
      });
    }

    // Any click that reaches root (background, around the image, on the scroller) closes the lightbox.
    this.root.addEventListener('click', function () {
      if (self._suppressNextRootClick) { self._suppressNextRootClick = false; return; }
      self.close();
    });

    document.addEventListener('keydown', function (e) {
      if (self.root.hasAttribute('hidden')) return;
      if (e.key === 'Escape') { e.preventDefault(); self.close(); }
      else if (e.key === 'ArrowLeft')  { e.preventDefault(); self.go(self.index - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); self.go(self.index + 1); }
      else if (e.key === 'Tab') { self.trapFocus(e); }
      else if (e.key === '+' || e.key === '=') { e.preventDefault(); self.toggleZoom(); }
      else if (e.key === '-' || e.key === '0') { e.preventDefault(); self.resetZoom(); }
    });

    if (this.image) {
      // Desktop: press → potential click or drag-to-pan, depending on movement
      this.image.addEventListener('mousedown', function (e) {
        if (isTouchDevice()) return;
        if (e.button !== 0) return;
        e.preventDefault();
        self.mouseDown = true;
        self.mouseDragged = false;
        self.mouseStart = {
          x: e.clientX, y: e.clientY,
          tx: self.translate.x, ty: self.translate.y
        };
      });
      document.addEventListener('mousemove', function (e) {
        if (!self.mouseDown || !self.mouseStart) return;
        var dx = e.clientX - self.mouseStart.x;
        var dy = e.clientY - self.mouseStart.y;
        if (!self.mouseDragged && Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD) {
          self.mouseDragged = true;
          if (self.scale > 1.001) self.scroller.classList.add('is-dragging');
        }
        if (self.mouseDragged && self.scale > 1.001) {
          self.translate.x = self.mouseStart.tx + dx;
          self.translate.y = self.mouseStart.ty + dy;
          self.applyTransform();
        }
      });
      document.addEventListener('mouseup', function (e) {
        if (!self.mouseDown) return;
        self.mouseDown = false;
        self.scroller.classList.remove('is-dragging');
        if (!self.mouseDragged && !self.root.hasAttribute('hidden')) {
          // It was a click (no drag) — toggle zoom at the cursor position
          self.toggleZoom(e);
          // Prevent the trailing root click from closing the lightbox
          self._suppressNextRootClick = true;
        }
        self.mouseStart = null;
      });

      // Touch — pinch zoom, double-tap, pan, swipe
      this.scroller.addEventListener('touchstart', function (e) { self.onLightboxTouchStart(e); }, { passive: false });
      this.scroller.addEventListener('touchmove',  function (e) { self.onLightboxTouchMove(e); },  { passive: false });
      this.scroller.addEventListener('touchend',   function (e) { self.onLightboxTouchEnd(e); });
    }
  };

  Lightbox.prototype.open = function (images, startIndex) {
    if (!images || !images.length) return;
    this.images = images;
    this.index = startIndex || 0;
    this._previouslyFocused = document.activeElement;
    // Move the lightbox to the end of <body> so it wins the z-index tie against
    // any third-party widget (e.g. live chat) that pins itself at the top of the stack.
    // appendChild on an existing node moves it; it doesn't duplicate.
    if (this.root.parentNode !== document.body || this.root !== document.body.lastElementChild) {
      document.body.appendChild(this.root);
    }
    this.root.removeAttribute('hidden');
    document.body.classList.add('product-lightbox-open');
    this.buildThumbs();
    this.render();
    if (this.totalEl) this.totalEl.textContent = String(images.length);
    var hasMulti = images.length > 1;
    if (this.prevBtn) this.prevBtn.style.display = hasMulti ? '' : 'none';
    if (this.nextBtn) this.nextBtn.style.display = hasMulti ? '' : 'none';
    if (this.thumbsEl) this.thumbsEl.style.display = hasMulti ? '' : 'none';
    var self = this;
    requestAnimationFrame(function () {
      self.root.classList.add('is-open');
      (self.closeBtn || self.image).focus({ preventScroll: true });
    });
  };

  Lightbox.prototype.close = function () {
    this.root.classList.remove('is-open');
    var self = this;
    var done = function () {
      self.root.setAttribute('hidden', '');
      document.body.classList.remove('product-lightbox-open');
      self.resetZoom();
      if (self._previouslyFocused && self._previouslyFocused.focus) {
        self._previouslyFocused.focus({ preventScroll: true });
      }
    };
    // Allow fade-out animation
    setTimeout(done, 200);
  };

  Lightbox.prototype.buildThumbs = function () {
    if (!this.thumbsEl) return;
    var html = '';
    for (var i = 0; i < this.images.length; i++) {
      var img = this.images[i];
      var activeClass = i === this.index ? ' is-active' : '';
      html += '<button type="button" class="product-lightbox__thumb' + activeClass +
              '" data-lb-thumb-index="' + i + '" aria-label="Vis billede ' + (i + 1) + '">' +
              '<img src="' + (img.thumb || img.src) + '" alt="" loading="lazy" decoding="async">' +
              '</button>';
    }
    this.thumbsEl.innerHTML = html;
  };

  Lightbox.prototype.updateActiveThumb = function () {
    if (!this.thumbsEl) return;
    var thumbs = this.thumbsEl.querySelectorAll('[data-lb-thumb-index]');
    for (var i = 0; i < thumbs.length; i++) {
      var active = i === this.index;
      thumbs[i].classList.toggle('is-active', active);
      if (active) {
        // Keep the active thumb scrolled into view
        var t = thumbs[i];
        var trackRect = this.thumbsEl.getBoundingClientRect();
        var tRect = t.getBoundingClientRect();
        if (tRect.left < trackRect.left || tRect.right > trackRect.right) {
          this.thumbsEl.scrollTo({
            left: t.offsetLeft - this.thumbsEl.clientWidth / 2 + t.offsetWidth / 2,
            behavior: 'smooth'
          });
        }
      }
    }
  };

  Lightbox.prototype.showLoading = function () {
    if (this.loader) this.loader.hidden = false;
  };
  Lightbox.prototype.hideLoading = function () {
    if (this.loader) this.loader.hidden = true;
  };

  Lightbox.prototype.render = function () {
    var img = this.images[this.index];
    if (!img || !this.image) return;
    var self = this;

    // Show a spinner until the full-size image has loaded
    this.showLoading();
    this.image.onload = function () { self.hideLoading(); };
    this.image.onerror = function () { self.hideLoading(); };
    this.image.alt = img.alt || '';
    this.image.src = img.src;
    // Already cached → no load event will fire, hide immediately
    if (this.image.complete && this.image.naturalWidth > 0) {
      this.hideLoading();
    }

    if (this.currentEl) this.currentEl.textContent = String(this.index + 1);
    this.updateActiveThumb();
    this.resetZoom();
  };

  Lightbox.prototype.go = function (target) {
    if (!this.images.length) return;
    target = ((target % this.images.length) + this.images.length) % this.images.length;
    this.index = target;
    this.render();
  };

  Lightbox.prototype.toggleZoom = function (originEvent) {
    if (this.scale > 1.001) {
      this.resetZoom();
    } else {
      this.setScale(LIGHTBOX_ZOOM_IN, originEvent);
    }
  };

  Lightbox.prototype.setScale = function (scale, originEvent) {
    scale = clamp(scale, 1, LIGHTBOX_MAX_SCALE);
    var prevScale = this.scale;
    this.scale = scale;
    // Adjust translate so the zoom focal point stays under cursor
    if (originEvent && this.image) {
      var rect = this.image.getBoundingClientRect();
      var cx = (originEvent.clientX - rect.left) - rect.width / 2;
      var cy = (originEvent.clientY - rect.top) - rect.height / 2;
      var factor = scale / prevScale;
      this.translate.x = (this.translate.x - cx) * factor + cx;
      this.translate.y = (this.translate.y - cy) * factor + cy;
    } else if (scale === 1) {
      this.translate = { x: 0, y: 0 };
    }
    this.applyTransform();
  };

  Lightbox.prototype.resetZoom = function () {
    this.scale = 1;
    this.translate = { x: 0, y: 0 };
    this.applyTransform();
  };

  // Keep the panned image within the viewport so it can never be dragged
  // entirely out of view. Allowed travel each axis is half the amount the
  // scaled image overflows the scroller; when it doesn't overflow, it stays centered.
  Lightbox.prototype.clampTranslate = function () {
    if (!this.image || !this.scroller) return;
    var maxX = Math.max(0, (this.image.offsetWidth * this.scale - this.scroller.clientWidth) / 2);
    var maxY = Math.max(0, (this.image.offsetHeight * this.scale - this.scroller.clientHeight) / 2);
    this.translate.x = clamp(this.translate.x, -maxX, maxX);
    this.translate.y = clamp(this.translate.y, -maxY, maxY);
  };

  Lightbox.prototype.applyTransform = function () {
    if (!this.image) return;
    this.clampTranslate();
    this.image.style.transform = 'translate3d(' + this.translate.x + 'px, ' + this.translate.y + 'px, 0) scale(' + this.scale + ')';
    this.scroller.classList.toggle('is-zoomed', this.scale > 1.001);
  };

  // Lightbox touch ---------------------------------------------------
  Lightbox.prototype.onLightboxTouchStart = function (e) {
    if (e.touches.length === 2) {
      e.preventDefault();
      this.pinchStartDistance = this.touchDistance(e.touches);
      this.pinchStartScale = this.scale;
      // Cancel any single-finger gesture and disable the CSS transition so the
      // image tracks the fingers in real time instead of lagging behind.
      this.panStart = null;
      this.swiping = false;
      this.scroller.classList.add('is-dragging');
      // Anchor the content point currently under the two-finger midpoint, so the
      // image both zooms about that point and pans as the midpoint moves.
      var sRect = this.scroller.getBoundingClientRect();
      this.scrollerCenter = { x: sRect.left + sRect.width / 2, y: sRect.top + sRect.height / 2 };
      var startMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      var startMidY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      this.pinchAnchor = {
        x: (startMidX - this.scrollerCenter.x - this.translate.x) / this.scale,
        y: (startMidY - this.scrollerCenter.y - this.translate.y) / this.scale
      };
    } else if (e.touches.length === 1) {
      var t = e.touches[0];
      this.touchStartX = t.clientX;
      this.touchStartY = t.clientY;
      var now = Date.now();
      var dt = now - this.lastTap;
      if (dt > 0 && dt < 300) {
        e.preventDefault();
        if (this.scale > 1.001) this.resetZoom();
        else this.setScale(LIGHTBOX_ZOOM_IN, t);
        this.lastTap = 0;
        return;
      }
      this.lastTap = now;
      if (this.scale > 1.001) {
        this.panStart = { x: t.clientX, y: t.clientY, tx: this.translate.x, ty: this.translate.y };
        this.scroller.classList.add('is-dragging');
      } else {
        this.swiping = true;
        this.swipeLocked = null;
      }
    }
  };

  Lightbox.prototype.onLightboxTouchMove = function (e) {
    if (e.touches.length === 2 && this.pinchStartDistance > 0) {
      e.preventDefault();
      var dist = this.touchDistance(e.touches);
      var newScale = clamp(this.pinchStartScale * (dist / this.pinchStartDistance), 1, LIGHTBOX_MAX_SCALE);
      var midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      var midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      // Keep the anchored content point under the moving midpoint → zoom and pan together.
      this.scale = newScale;
      this.translate.x = midX - this.scrollerCenter.x - this.pinchAnchor.x * newScale;
      this.translate.y = midY - this.scrollerCenter.y - this.pinchAnchor.y * newScale;
      this.applyTransform();
    } else if (e.touches.length === 1 && this.panStart) {
      e.preventDefault();
      this.translate.x = this.panStart.tx + (e.touches[0].clientX - this.panStart.x);
      this.translate.y = this.panStart.ty + (e.touches[0].clientY - this.panStart.y);
      this.applyTransform();
    } else if (e.touches.length === 1 && this.swiping) {
      var dx = e.touches[0].clientX - this.touchStartX;
      var dy = e.touches[0].clientY - this.touchStartY;
      if (!this.swipeLocked && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
        this.swipeLocked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (this.swipeLocked === 'y' && dy > 60) {
        // swipe down to close
        this.close();
        this.swiping = false;
      }
    }
  };

  Lightbox.prototype.onLightboxTouchEnd = function (e) {
    if (this.pinchStartDistance > 0 && e.touches.length < 2) {
      this.pinchStartDistance = 0;
      if (e.touches.length === 1 && this.scale > 1.001) {
        // One finger lifted while still zoomed — hand off to panning so the
        // remaining finger can reposition the image rather than doing nothing.
        var pt = e.touches[0];
        this.touchStartX = pt.clientX;
        this.touchStartY = pt.clientY;
        this.panStart = { x: pt.clientX, y: pt.clientY, tx: this.translate.x, ty: this.translate.y };
      } else {
        this.scroller.classList.remove('is-dragging'); // re-enable transition for the settle
        if (this.scale < 1.05) this.resetZoom();
      }
    }
    if (this.panStart && e.touches.length === 0) {
      this.panStart = null;
      this.scroller.classList.remove('is-dragging');
    }
    if (this.swiping && e.touches.length === 0) {
      var changedTouch = e.changedTouches[0];
      var dx = changedTouch.clientX - this.touchStartX;
      if (this.swipeLocked === 'x') {
        if (dx < -SWIPE_THRESHOLD) this.go(this.index + 1);
        else if (dx > SWIPE_THRESHOLD) this.go(this.index - 1);
      }
      this.swiping = false;
      this.swipeLocked = null;
    }
  };

  Lightbox.prototype.touchDistance = function (touches) {
    var dx = touches[0].clientX - touches[1].clientX;
    var dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  Lightbox.prototype.trapFocus = function (e) {
    var focusables = this.root.querySelectorAll('button:not([style*="display: none"])');
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /* ------------------------------------------------------------------ *
   * Bootstrap
   * ------------------------------------------------------------------ */
  function init() {
    var lightboxEl = document.querySelector('[data-product-lightbox]');
    if (lightboxEl && !window.__klassikLightbox) {
      window.__klassikLightbox = new Lightbox(lightboxEl);
    }
    var galleries = document.querySelectorAll('[data-product-gallery]');
    galleries.forEach(function (el) {
      if (!el.__galleryInstance) {
        el.__galleryInstance = new ProductGallery(el);
      }
    });

    // Public API for variant scripts: dispatch a CustomEvent with the new images
    //   document.dispatchEvent(new CustomEvent('product-gallery:set-images', {
    //     detail: { images: [<url template with {width}>, ...] }
    //   }));
    if (!window.__klassikGalleryHookBound) {
      window.__klassikGalleryHookBound = true;
      document.addEventListener('product-gallery:set-images', function (e) {
        var images = e && e.detail && e.detail.images;
        if (!images) return;
        var els = document.querySelectorAll('[data-product-gallery]');
        els.forEach(function (el) {
          if (el.__galleryInstance) el.__galleryInstance.setImages(images);
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
